/**
 * catalog-entry/render — 제품 등록 페이지의 그리기. 순수 함수(상태 → HTML 문자열).
 *
 * ③ 왼쪽 탭+목록(renderBulletinList) · ③ 오른쪽 한 편(renderBulletinDoc) · ① 목록(renderRequests).
 * CSS 접두 `ce-` = catalog-entry (kb- 칸반 · app-nav- 사이드바와 같은 페이지 접두).
 */
import { E_BULLETIN_VIEW_FILTERS } from '../../api/client';
import { ROW_NO_HEAD, rowNoCell } from '../../components/row-no';
import type { BulletinDocView, EBulletinStatus, EBulletinViewFilter, PendingPart } from '../../api/client';
import { escapeHtml, fmtDate, fmtDateTime } from '../../lib/cells';
import type { BulletinSection, RequestsSection } from './types';

// ─── 라벨 ───────────────────────────────────────────────────────────
/** 탭 다섯의 한국말 (VOCABULARY "제품 등록") */
const FILTER_LABELS: Record<EBulletinViewFilter, string> = {
  attention:       '사람 볼 것',
  check_failed:    '검사 실패',
  assemble_failed: '조립 실패',
  job_error:       '잡 오류',
  all:             '전부',
};

/** bulletin_doc.status 열 값의 한국말 — 칩에 보인다(원래 값은 title 로). V장 "불레틴 문서의 상태와 액션" 의 뜻 그대로 */
const STATUS_LABELS: Record<EBulletinStatus, string> = {
  unpacked:        '원문 있음',
  graded:          '등급 정함',
  drafted:         '초안 있음',
  fixed:           '고침 받음',
  needs_confirm:   '컨펌 대기',
  checked_ok:      '검사 통과',
  check_failed:    '검사 실패',
  assembled:       '조립됨',
  assemble_failed: '조립 실패',
  stale:           '새 판',
};

// ─── ③ 왼쪽 ─────────────────────────────────────────────────────────
/** ③ 왼쪽 — 탭 다섯 + 페이징 줄 + 목록(번호 · 세대 · status · unsure 수 · updated_at). 고른 줄에 active */
export function renderBulletinList(section: BulletinSection): string {
  const tabs = E_BULLETIN_VIEW_FILTERS.map((f) =>
    `<button type="button" class="ce-tab${f === section.filter ? ' active' : ''}" data-action="bulletin-filter" data-filter="${f}">${FILTER_LABELS[f]}</button>`);
  const pager = renderBulletinPager(section);
  const body = renderBulletinListBody(section);

  return `<div class="ce-tabs">${tabs.join('')}</div>${pager}${body}`;
}

function renderBulletinListBody(section: BulletinSection): string {
  if (section.loading)          return `<div class="text-muted ce-note">불러오는 중…</div>`;
  if (section.error)            return `<div class="ce-alert">${escapeHtml(section.error)}</div>`;
  if (section.rows.length === 0) return `<div class="text-muted ce-note">없습니다.</div>`;

  return renderBulletinRows(section);
}

/** 목록 한 줄 = 편 하나. 제목은 한 줄(넘치면 … · 전체 글은 title) — 나머지 칸은 폭 고정 · 줄바꿈 없음(2026-10-20) */
function renderBulletinRows(section: BulletinSection): string {
  const rows = section.rows.map((doc, index) => {
    const title = doc.title ?? '(제목 없음)';
    return `
    <tr class="ce-row${isSelected(section, doc) ? ' active' : ''}" data-action="bulletin-pick" data-vendor="${doc.vendor}" data-doc-no="${doc.doc_no}">
      ${rowNoCell(section.offset, index)}
      <td class="ce-col-no"><code>${doc.doc_no}</code></td>
      <td class="ce-col-title" title="${escapeHtml(title)}">${escapeHtml(title)}</td>
      <td class="ce-col-gen">${escapeHtml(doc.generation ?? '-')}</td>
      <td class="ce-col-status">${renderBulletinStatus(doc.status)}</td>
      <td class="ce-col-unsure num">${doc.unsure_rows ?? '-'}</td>
      <td class="ce-col-updated">${escapeHtml(shortDateTime(doc.updated_at))}</td>
    </tr>`;
  });

  return `
    <div class="deal-table-wrap">
      <table class="deal-items-table ce-table">
        <thead><tr>
          ${ROW_NO_HEAD}<th class="ce-col-no">번호</th><th class="ce-col-title">제목</th><th class="ce-col-gen">세대</th>
          <th class="ce-col-status">상태</th><th class="ce-col-unsure num">미확정</th><th class="ce-col-updated">갱신</th>
        </tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>`;
}

/** 'YYYY-MM-DD HH:mm' 에서 해를 뗀 'MM-DD HH:mm' — 목록 칸은 좁다. 한 편 머리는 fmtDateTime 그대로 */
function shortDateTime(iso: string): string {
  return fmtDateTime(iso).slice('YYYY-'.length);
}

/** 페이징 줄 — "N–M / total" + 이전(offset 0 이면 disabled) · 다음(offset + limit ≥ total 이면 disabled). 탭 밑, 목록 위 */
function renderBulletinPager(section: BulletinSection): string {
  if (section.total === 0) return '';

  const from = section.offset + 1;
  const to = Math.min(section.offset + section.limit, section.total);
  const bHasPrev = section.offset > 0;
  const bHasNext = to < section.total;

  return `
    <div class="ce-pager">
      <button type="button" class="btn-ghost" data-action="bulletin-prev" ${bHasPrev ? '' : 'disabled'}>이전</button>
      <span class="text-muted">${from}–${to} / ${section.total}</span>
      <button type="button" class="btn-ghost" data-action="bulletin-next" ${bHasNext ? '' : 'disabled'}>다음</button>
    </div>`;
}

function isSelected(section: BulletinSection, doc: BulletinDocView): boolean {
  return section.selected !== null && section.selected.vendor === doc.vendor && section.selected.doc_no === doc.doc_no;
}

/** 상태 칩 — 한국말 라벨, 원래 값은 title. 목록 · 한 편 · 미확정 파트 페이지가 같이 쓴다 */
export function renderBulletinStatus(status: EBulletinStatus): string {
  return `<span class="ce-status ce-status-${status}" title="${status}">${STATUS_LABELS[status]}</span>`;
}

// ─── ③ 오른쪽 ───────────────────────────────────────────────────────
/** ③ 오른쪽 — 한 편. check_error · job_error 글 그대로 + 다시 검사(check_failed 만 열림) + 레코드 페이지 링크(assembled 만). assemble_error 칸은 레코드 페이지로 옮겼다 */
export function renderBulletinDoc(section: BulletinSection): string {
  if (section.selected === null) return `<div class="text-muted ce-note">왼쪽에서 편을 고르세요.</div>`;

  const doc = section.selected;
  const canRecheck = doc.status === 'check_failed';
  const hasRecords = doc.status === 'assembled';   // 조립본 파일은 assembled 편에만 있다 — 그 밖은 /records 가 404

  return `
    <header class="ce-doc-head">
      <h2>${doc.vendor} ${doc.doc_no} <small>${escapeHtml(doc.generation ?? '')}</small></h2>
      ${renderBulletinStatus(doc.status)}
      <span class="text-muted">미확정 ${doc.unsure_rows ?? '-'} · ${escapeHtml(fmtDateTime(doc.updated_at))}</span>
    </header>
    <div class="ce-doc-title">${escapeHtml(doc.title ?? '(제목 없음)')}</div>
    ${renderErrorBlock('check_error — revise 검사가 걸린 자리', doc.check_error)}
    ${renderErrorBlock('job_error — 잡이 낸 마지막 예외', doc.job_error)}
    <div class="ce-actions">
      <button type="button" class="btn-primary" data-action="bulletin-recheck" ${canRecheck ? '' : 'disabled title="check_failed 에서만"'}>다시 검사</button>
      ${hasRecords ? `<a class="btn-ghost" data-link href="/catalog-entry/bulletin/${doc.vendor}/${doc.doc_no}/records">레코드 보기</a>` : ''}
      <a class="btn-ghost" data-link href="/catalog-entry/bulletin/${doc.vendor}/${doc.doc_no}/unsure">상세</a>
    </div>`;
}

function renderErrorBlock(title: string, textOrNull: string | null): string {
  if (textOrNull === null) return '';

  return `<section class="ce-error"><label>${title}</label><pre>${escapeHtml(textOrNull)}</pre></section>`;
}

// ─── ① 등록 요청 ────────────────────────────────────────────────────
/** ① — catalog_part_pending 목록. 읽기만 (구 PendingPartsView 의 표 그대로) */
export function renderRequests(section: RequestsSection): string {
  if (section.loading)           return `<div class="text-muted ce-note">불러오는 중…</div>`;
  if (section.error)             return `<div class="ce-alert">${escapeHtml(section.error)}</div>`;
  if (section.rows.length === 0) return `<div class="text-muted ce-note">등록 요청이 없습니다.</div>`;

  const rows = section.rows.map((r: PendingPart, index: number) => `
    <tr>
      ${rowNoCell(0, index)}
      <td><code>${escapeHtml(r.option_part ?? '-')}</code></td>
      <td>${escapeHtml(r.description ?? '-')}</td>
      <td>${escapeHtml(r.catalog_table_name ?? '-')}</td>
      <td>${escapeHtml(r.status)}</td>
      <td>${escapeHtml(fmtDate(r.created_at))}</td>
    </tr>`);

  return `
    <div class="deal-table-wrap">
      <table class="deal-items-table">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>디스크립션</th><th>카테고리</th><th>상태</th><th>등록일</th></tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>`;
}
